<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { evaluateSLAStatus } from '@chatwoot/utils';
import SLAPopoverCard from './SLAPopoverCard.vue';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showExtendedInfo: {
    type: Boolean,
    default: false,
  },
  parentWidth: {
    type: Number,
    default: 1000,
  },
});

const REFRESH_INTERVAL = 60000;
const PIE_INTERVAL = 10000;
const { t } = useI18n();

const timer = ref(null);
const slaStatus = ref({
  threshold: null,
  isSlaMissed: false,
  type: null,
  icon: null,
});

const appliedSLA = computed(() => props.chat?.applied_sla);
const slaEvents = computed(() => props.chat?.sla_events);
const hasSlaThreshold = computed(() => slaStatus.value?.threshold);
const isSlaMissed = computed(() => slaStatus.value?.isSlaMissed);

// Reactive clock — atualiza a cada 10s para o pie ficar fluido
const nowSeconds = ref(Math.floor(Date.now() / 1000));
const pieTimer = ref(null);

const slaStatusText = computed(() => {
  const upperCaseType = slaStatus.value?.type?.toUpperCase();
  const statusKey = isSlaMissed.value ? 'MISSED' : 'DUE';

  return t(`CONVERSATION.HEADER.SLA_STATUS.${upperCaseType}`, {
    status: t(`CONVERSATION.HEADER.SLA_STATUS.${statusKey}`),
  });
});

const showSlaPopoverCard = computed(
  () => props.showExtendedInfo && slaEvents.value?.length > 0
);

const groupClass = computed(() => {
  return props.showExtendedInfo
    ? 'h-[26px] rounded-lg bg-n-alpha-1'
    : 'rounded h-5 border border-n-strong';
});

// Pie clock: determina qual fase do SLA está ativa e o start correto
const activeSlaPhase = computed(() => {
  const applied = appliedSLA.value;
  const chat = props.chat;
  if (!applied) return null;

  // FRT: agente ainda não respondeu
  if (
    !chat.first_reply_created_at &&
    applied.sla_first_response_time_threshold
  ) {
    return {
      start: chat.created_at,
      target: applied.sla_first_response_time_threshold,
    };
  }
  // NRT: agente já respondeu, cliente enviou nova mensagem e está aguardando
  if (chat.waiting_since && applied.sla_next_response_time_threshold) {
    return {
      start: chat.waiting_since,
      target: applied.sla_next_response_time_threshold,
    };
  }
  // RT: aguardando resolução
  if (applied.sla_resolution_time_threshold) {
    return {
      start: chat.created_at,
      target: applied.sla_resolution_time_threshold,
    };
  }
  return null;
});

const slaPercent = computed(() => {
  const phase = activeSlaPhase.value;
  if (!phase || !phase.start || !phase.target) return 0;
  const elapsed = Math.max(0, nowSeconds.value - phase.start);
  return Math.min(1, elapsed / phase.target);
});

const slaTextStyles = computed(() => {
  if (isSlaMissed.value || slaPercent.value >= 1) return 'text-n-ruby-11';
  if (slaPercent.value >= 0.75) return 'text-orange-500';
  return 'text-n-amber-11';
});

const piePath = computed(() => {
  const p = slaPercent.value;
  const cx = 6;
  const cy = 6;
  const r = 5;
  const angle = p * 2 * Math.PI;
  const x = cx + r * Math.sin(angle);
  const y = cy - r * Math.cos(angle);
  const largeArc = p > 0.5 ? 1 : 0;
  return `M ${cx} ${cy} L ${cx} ${cy - r} A ${r} ${r} 0 ${largeArc} 1 ${x.toFixed(3)} ${y.toFixed(3)} Z`;
});

const updateSlaStatus = () => {
  slaStatus.value = evaluateSLAStatus({
    appliedSla: appliedSLA.value,
    chat: props.chat,
  });
};

const createTimer = () => {
  timer.value = setTimeout(() => {
    updateSlaStatus();
    createTimer();
  }, REFRESH_INTERVAL);
};

const createPieTimer = () => {
  pieTimer.value = setTimeout(() => {
    nowSeconds.value = Math.floor(Date.now() / 1000);
    createPieTimer();
  }, PIE_INTERVAL);
};

watch(
  () => props.chat,
  () => {
    updateSlaStatus();
  }
);

const slaPopoverClass = computed(() => {
  return props.showExtendedInfo
    ? 'ltr:pr-1.5 rtl:pl-1.5 ltr:border-r rtl:border-l border-n-strong'
    : '';
});

onMounted(() => {
  updateSlaStatus();
  createTimer();
  createPieTimer();
});

onUnmounted(() => {
  if (timer.value) clearTimeout(timer.value);
  if (pieTimer.value) clearTimeout(pieTimer.value);
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="hasSlaThreshold"
    class="relative flex items-center cursor-pointer min-w-fit group"
    :class="groupClass"
  >
    <div
      class="flex items-center w-full truncate px-1.5"
      :class="showExtendedInfo ? '' : 'gap-1'"
    >
      <div class="flex items-center gap-1" :class="slaPopoverClass">
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          class="flex-shrink-0"
          :class="slaTextStyles"
        >
          <circle cx="6" cy="6" r="5" fill="currentColor" opacity="0.25" />
          <path
            v-if="slaPercent > 0 && slaPercent < 1"
            :d="piePath"
            fill="currentColor"
          />
          <circle
            v-else-if="slaPercent >= 1"
            cx="6"
            cy="6"
            r="5"
            fill="currentColor"
          />
        </svg>
        <span
          v-if="showExtendedInfo && parentWidth > 650"
          class="text-xs font-medium"
          :class="slaTextStyles"
        >
          {{ slaStatusText }}
        </span>
      </div>
      <span
        class="text-xs font-medium"
        :class="[slaTextStyles, showExtendedInfo && 'ltr:pl-1.5 rtl:pr-1.5']"
      >
        {{ slaStatus.threshold }}
      </span>
    </div>
    <SLAPopoverCard
      v-if="showSlaPopoverCard"
      :sla-missed-events="slaEvents"
      class="start-0 xl:start-auto xl:end-0 top-7 hidden group-hover:flex"
    />
  </div>
</template>
